import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  Component,
  inject,
  Inject,
  signal
} from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ModelService, PluginInjectorService, PluginInjectorWidget } from 'core-client/api';
import { NgsildConnectorService } from 'ngsild-connector';
import { WmsChartService } from 'wms-chart-loader';
import { LeafletService } from '../lib/leaflet/leaflet.service';
import * as NgsildConstants from '../lib/ngsild/helpers/ngsild.constants';
import {
  IMapWidgetFormGroupEntities
} from './map-widget-settings.interface';
import { INgsiEntityIdAttrs } from '../lib/ngsild/interfaces/ngsi-entity.interface';
import { MapWidgetPermissionsService } from '../../shared/services/helpers/map-widget-permissions.service';
import { IMapWidgetPermissions } from '../../shared/interfaces/map-widget-permission.interface';
import { WmsChartServer } from '../lib/leaflet/providers/wms-chart-server';
import { OpenStreetMap } from '../lib/leaflet/providers/open-street-map';
import { OpenSeaMap } from '../lib/leaflet/providers/open-sea-map';
import { WidgetSettingsObserveService } from '../../shared/services/helpers/widget-settings-observe.service';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import { ILeafletInstance } from '../lib/leaflet/leaflet.interface';
import * as leafletInstanceConfig from '../lib/ngsild/helpers/leaflet-instance-config.constants';
import {
  IMapWidget,
  IWidgetHeaderOptions,
  IRouteTrackingSettings,
  ISubscriptionFlagsSettings
} from '../../shared/interfaces/map-widget.interface';
import { IExtendedAppPluginWidget } from '../../shared/interfaces/extended-app-plugin-widget.interface';

@Component({
  selector: 'lib-map-widget-settings',
  imports: [SharedModule],
  templateUrl: './map-widget-settings.component.html',
  styleUrl: './map-widget-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapWidgetSettingsComponent implements AfterViewInit, OnDestroy {
  // Services
  private readonly dialogRef = inject(MatDialogRef<MapWidgetSettingsComponent>);
  private readonly translate = inject(TranslateService);
  private readonly modelService = inject(ModelService);
  private readonly leafletService = inject(LeafletService);
  private readonly ngsildConnectorService = inject(NgsildConnectorService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly pluginInjectorService = inject(PluginInjectorService);
  private readonly wmsChartService = inject(WmsChartService);
  private readonly widgetSettingObserve = inject(WidgetSettingsObserveService);
  private readonly announcer: LiveAnnouncer = inject(LiveAnnouncer);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  // Variables
  private leafletInstance: ILeafletInstance | undefined;
  protected readonly ngsiDataSource = leafletInstanceConfig.config.dataSourceSelector;
  protected geolocationSource: string[] = NgsildConstants.geolocationSource;
  protected activeTileLayers: string[] = ['OSM', 'OpenSeaMap'];
  private selectedEntityIdsAttrs: INgsiEntityIdAttrs[] = [{
    prefix: NgsildConstants.NgsiIdPrefix,
    type: null,
    owner: null,
    idPattern: null
  }];

  protected mapWidget: IMapWidget;
  protected widgetId: string;
  protected currDashboardId: string | undefined;
  protected widgetPermissions: IMapWidgetPermissions = MapWidgetPermissionsService.widgetPermissions;

  // Widget settings variables
  protected selectedWidgetHeaderOption: string = 'alwaysDisplayed';
  protected widgetTitle: string;
  protected showWidgetTitle: boolean = false;
  protected selectedEntities: { id: string }[] = [];
  protected selectedDataSourceIdentifier: string = leafletInstanceConfig.config.dataSourceSelector.MMSI.label;
  protected selectedDataSourceTypeCategory: string = 'AIS';
  protected selectedTileLayer: string = 'OSM';
  protected selectedLatitude: number = 54.328808;
  protected selectedLongitude: number = 10.159384;
  protected selectedZoom: number = 10;
  protected centerMapOnMarkers: boolean = false;
  protected subscriptionFlags: ISubscriptionFlagsSettings = {
    enableAisTargets: false,
    enableAisPrediction: false,
    enableFusionMaritimeTarget: false,
    enableMaritimeTargets: false,
    enablePlannedPath: false,
    enableRadarTarget: false,
  };
  protected routeTrackingSettings: IRouteTrackingSettings = {
    enableRouteTracking: false,
    durationTime: null,
    maxTrackPoints: null,
    radius: null
  };

  // signal multi-select
  readonly addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  readonly entities = signal<{ id: string }[]>([]);

  constructor(@Inject(MAT_DIALOG_DATA) private readonly data: PluginInjectorWidget.IWidget) {
    this.mapWidget = data.appPluginWidget;
    this.widgetId = data.appPluginWidget._id;
    this.widgetTitle = data.appPluginWidget.name;
    this.showWidgetTitle = data.appPluginWidget.settings.showWidgetTitle;
    const widgetHeaderOption = data.appPluginWidget.settings.widgetHeader;
    const widgetHeaderOptions = Object.keys(widgetHeaderOption) as IWidgetHeaderOptions[];
    this.selectedWidgetHeaderOption = widgetHeaderOptions.filter(
      (key: IWidgetHeaderOptions) => widgetHeaderOption[key]
    )[0];
  }

  async ngAfterViewInit(): Promise<void> {
    const user = this.modelService._user.getUser();

    if (!user?._id) {
      console.error(`[MAP Widget] User not found.`);
      return;
    }

    this.widgetPermissions = await MapWidgetPermissionsService.getUserAccessRights(
      user,
      this.pluginInjectorService,
      this.data
    );

    this.currDashboardId = user.active_dashboard;

    WmsChartServer.wmsLayerEnabled = this.widgetPermissions.wmsChartLoaderEnabled;

    this.selectedEntities = this.mapWidget.settings?.entities ?? [];

    if (this.mapWidget.settings?.dataSourceIdentifier) {
      this.selectedDataSourceIdentifier = this.mapWidget.settings.dataSourceIdentifier;
    }

    if (this.mapWidget.settings?.dataSourceTypeCategory) {
      this.selectedDataSourceTypeCategory = this.mapWidget.settings.dataSourceTypeCategory;
    }

    if (this.mapWidget.settings?.baseTileLayer) {
      this.selectedTileLayer = this.mapWidget.settings.baseTileLayer;
    }

    if (this.mapWidget.settings?.latitude) {
      this.selectedLatitude = this.mapWidget.settings.latitude;
    }

    if (this.mapWidget.settings?.longitude) {
      this.selectedLongitude = this.mapWidget.settings.longitude;
    }

    if (this.mapWidget.settings?.zoom) {
      this.selectedZoom = this.mapWidget.settings.zoom;
    }

    this.centerMapOnMarkers = this.mapWidget.settings?.centerMapOnMarkers ?? false;
    this.subscriptionFlags.enableAisTargets = this.mapWidget.settings?.enableAisTargets ?? false;
    this.subscriptionFlags.enableAisPrediction = this.mapWidget.settings?.enableAisPrediction ?? false;
    this.subscriptionFlags.enableFusionMaritimeTarget = this.mapWidget.settings?.enableFusionMaritimeTarget ?? false;
    this.subscriptionFlags.enableMaritimeTargets = this.mapWidget.settings?.enableMaritimeTargets ?? false;
    this.subscriptionFlags.enablePlannedPath = this.mapWidget.settings?.enablePlannedPath ?? false;
    this.subscriptionFlags.enableRadarTarget = this.mapWidget.settings?.enableRadarTarget ?? false;

    this.routeTrackingSettings.enableRouteTracking = this.mapWidget.settings?.enableRouteTracking ?? false;
    this.routeTrackingSettings.durationTime = this.mapWidget.settings?.durationTime;
    this.routeTrackingSettings.maxTrackPoints = this.mapWidget.settings?.maxTrackPoints;
    this.routeTrackingSettings.radius = this.mapWidget.settings?.radius;

    this.selectedEntityIdsAttrs = this.selectedEntities.map(
      (settingEntity: IMapWidgetFormGroupEntities) =>
        NgsildConstants.filterNgsiEntityIdAttrs(settingEntity.id, this.selectedDataSourceIdentifier));

    const formatedEntities = this.selectedEntityIdsAttrs.map((entity: INgsiEntityIdAttrs) => {
      return { id: entity.idPattern };
    }) as unknown as { id: string }[];

    this.entities.set(formatedEntities);
    await this.loadPreviewMap();
  }

  private getMapWidgetId(widgetId: string): string {
    return `settings_${widgetId}`;
  }

  private async loadPreviewMap(): Promise<void> {
    const mapId = this.getMapWidgetId(this.widgetId);
    this.leafletService.createInstance(mapId);
    this.leafletInstance = this.leafletService.getInstance(mapId);

    if (!this.currDashboardId) {
      console.error('[MAP Settings] Failed to load preview map: Dashboard ID not found');
      return;
    }

    if (!this.leafletInstance) {
      console.error('[MAP Settings] Failed to create Leaflet instance');
      return;
    }

    // ALWAYS add OSM and OpenSeaMap immediately (no waiting)
    OpenStreetMap.addOSMBaseLayer(mapId, this.leafletInstance);
    OpenSeaMap.addSeaMapLayer(mapId, this.leafletInstance);

    // Handle WMS layer (non-blocking)
    if (WmsChartServer.wmsLayerEnabled) {
      if (WmsChartServer.wmsLayerToken == null) {
        // Don't await - let it run in background
        WmsChartServer.registerWidget(this.mapWidget, this.currDashboardId,this.wmsChartService)
          .then((token: string | null) => {
            if (token && this.leafletInstance) {
              WmsChartServer.addWmsLayer(
                mapId,
                token,
                this.wmsChartService,
                this.leafletInstance
              );

              // Add to active layers if not already there
              if (!this.activeTileLayers.includes('WMS-Chart')) {
                this.activeTileLayers.push('WMS-Chart');
              }

              // If user selected WMS, activate it now
              if (this.selectedTileLayer === 'WMS-Chart') {
                this.leafletService.activateLayer('WMS-Chart', this.leafletInstance);
              }

              this.cdr.detectChanges();
            }
          })
          .catch(err => console.warn('[MAP Settings] WMS registration failed:', err));
      } else {
        // Token already cached - add WMS layer immediately
        WmsChartServer.addWmsLayer(
          mapId,
          WmsChartServer.wmsLayerToken,
          this.wmsChartService,
          this.leafletInstance
        );

        if (!this.activeTileLayers.includes('WMS-Chart')) {
          this.activeTileLayers.push('WMS-Chart');
        }
      }
    }

    // Continue immediately with layer activation (don't wait for WMS)
    this.activateSelectedLayer();
    this.leafletService.addLayerControl(mapId, this.leafletInstance);
    this.leafletService.initMap(this.leafletInstance);
  }

  /**
   * Activate selected layer with fallback
   * If WMS selected but not available, fall back to OSM
   */
  private activateSelectedLayer(): void {
    if (!this.leafletInstance) {
      return;
    }

    const hasWmsToken = WmsChartServer.wmsLayerToken != null;

    if (this.selectedTileLayer === 'WMS-Chart') {
      if (WmsChartServer.wmsLayerEnabled && hasWmsToken) {
        // WMS available - activate it
        this.leafletService.activateLayer('WMS-Chart', this.leafletInstance);
      } else {
        // WMS not available yet - fall back to OSM
        console.warn('[MAP Settings] WMS selected but not available yet, falling back to OSM');
        this.leafletService.activateLayer('OSM', this.leafletInstance);
        // Note: Don't change selectedTileLayer - user preference is WMS
        // It will be activated when token arrives
      }
    } else if (this.selectedTileLayer === 'OpenSeaMap') {
      // OpenSeaMap requires OSM base layer
      this.leafletService.activateLayer('OSM', this.leafletInstance);
      this.leafletService.activateLayer('OpenSeaMap', this.leafletInstance);
    } else {
      // Standard layer (OSM)
      this.leafletService.activateLayer(this.selectedTileLayer, this.leafletInstance);
    }
  }

  async onSubmit(): Promise<void> {
    const entitiesAsString: string[] = this.selectedEntities.map((entityId: {id: string} | string): string => {
      if (typeof entityId === 'string') {
        return entityId;
      } else {
        return entityId.id;
      }
    })

    const selectedEntityIdsAttrs = entitiesAsString.map((entityId: string): INgsiEntityIdAttrs => {
      return NgsildConstants.filterNgsiEntityIdAttrs(entityId,this.selectedDataSourceIdentifier);
    });

    const formatedEntities = NgsildConstants.formatIdsToNgsiIdFormat(
      entitiesAsString,
      selectedEntityIdsAttrs
    );

    // Update widget settings
    this.mapWidget.name = this.widgetTitle;
    this.mapWidget.settings.showWidgetTitle = this.showWidgetTitle;
    this.mapWidget.settings.entities = formatedEntities;
    this.mapWidget.settings.dataSourceIdentifier = this.selectedDataSourceIdentifier;
    this.mapWidget.settings.dataSourceTypeCategory = this.selectedDataSourceTypeCategory;
    this.mapWidget.settings.baseTileLayer = this.selectedTileLayer;
    this.mapWidget.settings.latitude = this.selectedLatitude;
    this.mapWidget.settings.longitude = this.selectedLongitude;
    this.mapWidget.settings.zoom = this.selectedZoom;
    this.mapWidget.settings.centerMapOnMarkers = this.centerMapOnMarkers;
    this.mapWidget.settings.enableAisTargets = this.subscriptionFlags.enableAisTargets;
    this.mapWidget.settings.enableAisPrediction = this.subscriptionFlags.enableAisPrediction;
    this.mapWidget.settings.enableFusionMaritimeTarget = this.subscriptionFlags.enableFusionMaritimeTarget;
    this.mapWidget.settings.enableMaritimeTargets = this.subscriptionFlags.enableMaritimeTargets;
    this.mapWidget.settings.enablePlannedPath = this.subscriptionFlags.enablePlannedPath;
    this.mapWidget.settings.enableRadarTarget = this.subscriptionFlags.enableRadarTarget;
    this.mapWidget.settings.enableRouteTracking = this.routeTrackingSettings.enableRouteTracking;
    this.mapWidget.settings.durationTime = this.routeTrackingSettings.durationTime;
    this.mapWidget.settings.maxTrackPoints = this.routeTrackingSettings.maxTrackPoints;
    this.mapWidget.settings.radius = this.routeTrackingSettings.radius;

    console.log('[MAP Widget Settings] Save data:', this.mapWidget.name, this.mapWidget.settings);

    await this.modelService._appPluginWidget.upsertOne(this.mapWidget);

    // handle subscriptions
    if (this.mapWidget.settings?.entities?.length > 0) {
      const appPluginWidget = this.mapWidget as IExtendedAppPluginWidget;

      await NgsildConstants.manageNgsiSubscriptions(
        appPluginWidget,
        selectedEntityIdsAttrs,
        this.widgetPermissions,
        this.ngsildConnectorService,
        this.modelService
      );
    }

    // Update widget name
    // Directly data-binding changes
    const widget = this.pluginInjectorService.widgetService.getWidgetItem(this.mapWidget._id);
    if (widget) {
      // activate observables
      this.widgetSettingObserve.activeMapSettingObservable(this.mapWidget, widget);
      this.showSaveSuccessSnackbar();
      this.dialogRef.close();
    } else {
      this.showSaveErrorSnackbar();
    }
  }

  onZoomLevelChange($event: any): void {
    const zoomLevel = $event.target.value
    if (zoomLevel !== 0 && zoomLevel !== 20) {
      this.leafletService.updateZoom( zoomLevel, this.leafletInstance)
      this.leafletService.initMap(this.leafletInstance)
    }
  }

  routeTrackingEnabled(): boolean {
    return this.routeTrackingSettings.enableRouteTracking ?? false;
  }

  private showSaveSuccessSnackbar(): void {
    this.snackBar.open(this.translate.instant('MAP.SUCCESSFUL_SETTINGS_SAVE'), '', this.getSnackbarConfig('success-snackbar'));
  }

  private showSaveErrorSnackbar(): void {
    this.snackBar.open(this.translate.instant('MAP.ERROR_SETTINGS_SAVE'), '', this.getSnackbarConfig('error-snackbar'));
  }

  private getSnackbarConfig(panelClass: string): MatSnackBarConfig {
    return {
      duration: 2000,
      panelClass: panelClass,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    };
  }

  ngOnDestroy(): void {
    const mapId = this.getMapWidgetId(this.widgetId);

    // Cleanup leaflet instance
    if (this.leafletService.mapInstances) {
      this.leafletService.removeInstance(mapId);
    }

    // Unregister using correct map ID (settings_xxx, not widget ID)
    if (this.mapWidget?._id) {
      this.wmsChartService.unregisterNotificationHandler(
        mapId,  // Use settings map ID, not widget ID
        <string>this.mapWidget?.class
      );
    }
  }

  selectLayer($event: MatSelectChange): void {
    const mapId: string = this.getMapWidgetId(this.widgetId);
    const selectedLayer = $event.value;
    const leafletInstance = this.leafletInstance;

    // Remove unselected layers except when OpenSeaMap is selected and OSM is also active
    const unselectedLayers = this.activeTileLayers.filter((layer: string) => layer !== selectedLayer);
    unselectedLayers.forEach((layer: string) => {
      if (!(selectedLayer === 'OpenSeaMap' && layer === 'OSM')) {
        this.leafletService.removeTileLayer(layer, leafletInstance);
      }
    });

    // Set zoom based on selected layer
    const activeTileLayers = leafletInstance?.tileLayers;

    const selectedLayerZoom = activeTileLayers?.[selectedLayer]?.zoom;
    if (selectedLayerZoom) {
      this.selectedZoom = selectedLayerZoom;
    }

    // Activate necessary layers based on selected layer
    if (selectedLayer === 'OpenSeaMap') {
      this.leafletService.activateLayer('OSM', leafletInstance);
    }

    if (selectedLayer === 'WMS-Chart' && WmsChartServer.wmsLayerEnabled && WmsChartServer.wmsLayerToken != null) {
      const wmsTileLayer = leafletInstance?.tileLayers['WMS-Chart']?.layer;
      if (!wmsTileLayer) {
        WmsChartServer.addWmsLayer(
          mapId,
          WmsChartServer.wmsLayerToken,
          this.wmsChartService ,
          leafletInstance
        );
      }
    }

    this.leafletService.activateLayer(selectedLayer, leafletInstance);
    this.leafletService.addLayerControl(mapId, leafletInstance);

    this.selectedTileLayer = selectedLayer;
  }

  selectHeaderOption($event: MatSelectChange): void {
    const headerOptions: Record<string, { alwaysDisplayed: boolean; onHoverDisplayed: boolean; alwaysOff: boolean; transparent: boolean }> = {
      alwaysDisplayed: { alwaysDisplayed: true, onHoverDisplayed: false, alwaysOff: false, transparent: false },
      onHoverDisplayed: { alwaysDisplayed: false, onHoverDisplayed: true, alwaysOff: false, transparent: false },
      alwaysOff: { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: true, transparent: false },
      transparent: { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: false, transparent: true }
    };

    if ($event.value in headerOptions) {
      this.mapWidget.settings.widgetHeader = headerOptions[$event.value];
    }
  }

  getText(key: unknown): string {
    const translations: Record<string, string> = {
      alwaysDisplayed: 'MAP.WIDGET_HEADER.ALWAYS_DISPLAYED',
      onHoverDisplayed: 'MAP.WIDGET_HEADER.ON_HOVER_DISPLAYED',
      alwaysOff: 'MAP.WIDGET_HEADER.ALWAYS_OFF',
      transparent: 'MAP.WIDGET_HEADER.TRANSPARENT'
    };

    return this.translate.instant(translations[key as string] || 'unknown');
  }

  selectNgsiDataSource($event: MatSelectChange): void {
    this.selectedDataSourceIdentifier = $event.value

    if (this.selectedDataSourceIdentifier === leafletInstanceConfig.config.dataSourceSelector.MMSI.label) {
      this.selectedDataSourceTypeCategory = NgsildConstants.geolocationSource[0];
    }

    if (this.selectedDataSourceIdentifier === leafletInstanceConfig.config.dataSourceSelector.TrackID.label) {
      this.selectedDataSourceTypeCategory = NgsildConstants.geolocationSource[2];
    }
  }

  disableDataSourceTypeCategory(source: string) {
    if (this.selectedDataSourceIdentifier === leafletInstanceConfig.config.dataSourceSelector.MMSI.label) {
      return !(source === 'AIS' || source === 'GLL');
    }

    if (this.selectedDataSourceIdentifier === leafletInstanceConfig.config.dataSourceSelector.TrackID.label) {
      return source !== 'Live'
    }

    return false
  }

  showMaritimeTargetSelector(): boolean {
    const visible = this.selectedDataSourceIdentifier !== 'TrackID';
    return this.widgetPermissions.maritimeTargetsDataEnabled && visible
  }

  showFusionMaritimeSelector(): boolean {
    const visible = this.selectedDataSourceIdentifier !== 'TrackID';
    return this.widgetPermissions.fusionMaritimeTargetDataEnabled && visible
  }

  showAISTargetsSelector(): boolean {
    const visible = this.selectedDataSourceIdentifier !== 'TrackID';
    return this.widgetPermissions.aisTargetsDataEnabled && visible;
  }

  showRadarTargetsSelector(): boolean {
    const visible = this.selectedDataSourceIdentifier !== 'TrackID';
    return this.widgetPermissions.radarTargetDataEnabled && visible;
  }

  showAISPredictTrackingLine(): boolean {
    const visible = this.selectedDataSourceIdentifier === 'MMSI' || this.selectedDataSourceIdentifier === 'TrackID';
    return this.widgetPermissions.aisPredictionDataEnabled && visible;
  }

  showPlannedPath(): boolean {
    const visible = this.selectedDataSourceIdentifier !== 'TrackID';
    return this.widgetPermissions.plannedPathDataEnabled && visible;
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    // Add our fruit
    if (value) {
      this.entities.update(entities => [...entities, {id: value}]);
    }

    // Clear the input value
    event.chipInput.clear();
  }

  remove(entity: IMapWidgetFormGroupEntities): void {
    this.entities.update(entities => {
      const index = entities.indexOf(entity);
      if (index < 0) {
        return entities;
      }

      entities.splice(index, 1);
      this.announcer.announce(`Removed ${entity.id}`);
      return [...entities];
    });
  }

  edit(entity: IMapWidgetFormGroupEntities, event: MatChipEditedEvent) {
    const value = event.value.trim();

    // Remove fruit if it no longer has a name
    if (!value) {
      this.remove(entity);
      return;
    }

    // Edit existing entities
    this.entities.update(entities => {
      const index = entities.indexOf(entity);
      if (index >= 0) {
        entities[index].id = value;
        return [...entities];
      }
      return entities;
    });
  }
}
